import React, { useCallback, useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import CloseIcon from '@mui/icons-material/Close';
import dayjs from 'dayjs';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AxiosInstance from '../../services/networkServices';
import { uploadImage, getUploadedMediaRecord } from '../../http/uploads';
import PageLayout from '../../components/PageLayout';
import CommonPagination from '../../components/Table/CommonPagination';

const ROWS_PER_PAGE = 20;
const MEDIA_CAPTION_MAX = 500;
const TEXT_POST_MAX = 140;
const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
const ACCEPTED_TYPES = ['image/jpeg', 'image/jpg', 'image/png'];

const headCellSx = {
  fontWeight: 600,
  color: '#000',
  backgroundColor: '#e0e0e0',
  position: 'sticky',
  top: -1,
  zIndex: 1,
};

/** Natural width/height of a local image file (for the post's mediaWidth/Height). */
const readImageSize = (file) =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
      URL.revokeObjectURL(url);
    };
    img.onerror = () => {
      resolve(null);
      URL.revokeObjectURL(url);
    };
    img.src = url;
  });

const NewPostDialog = ({ open, onClose, onPublished }) => {
  const [caption, setCaption] = useState('');
  const [photo, setPhoto] = useState(null); // { file, previewUrl, size }
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) {
      setCaption('');
      setPhoto(null);
      setSubmitting(false);
      setError('');
    }
  }, [open]);

  useEffect(
    () => () => {
      if (photo?.previewUrl) URL.revokeObjectURL(photo.previewUrl);
    },
    [photo]
  );

  const captionMax = photo ? MEDIA_CAPTION_MAX : TEXT_POST_MAX;
  const overLimit = caption.length > captionMax;
  const canPost = (photo || caption.trim().length > 0) && !overLimit && !submitting;

  const handleFileSelected = async (file) => {
    if (!file) return;
    if (!ACCEPTED_TYPES.includes(file.type)) {
      setError('Photos must be JPEG or PNG');
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      setError('Photo must be under 10MB');
      return;
    }
    setError('');
    const size = await readImageSize(file);
    setPhoto({ file, previewUrl: URL.createObjectURL(file), size });
  };

  const handleSubmit = async () => {
    if (!canPost) return;
    setSubmitting(true);
    setError('');
    try {
      const payload = { caption: caption.trim() };

      if (photo) {
        const formData = new FormData();
        formData.append('type', 'image');
        formData.append('file', photo.file);
        formData.append('folder', 'asset');
        formData.append('metadata', JSON.stringify({ type: 'DIRECT_POST_UPLOAD' }));
        const res = await uploadImage(formData);
        const media = getUploadedMediaRecord(res);
        if (!media?._id) {
          throw new Error('Photo upload failed. Please try again.');
        }
        payload.mediaId = media._id;
        if (photo.size) {
          payload.mediaWidth = photo.size.width;
          payload.mediaHeight = photo.size.height;
        }
      }

      await AxiosInstance.post('/admin/pagent-posts', payload);
      onPublished();
      onClose();
    } catch (err) {
      console.error('Error publishing @pagent post:', err);
      setError(err?.response?.data?.message || err.message || 'Failed to publish post');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>New post as @pagent</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {photo ? (
            <Box sx={{ position: 'relative', alignSelf: 'flex-start', maxWidth: '100%' }}>
              <img
                src={photo.previewUrl}
                alt="Selected"
                style={{
                  maxWidth: '100%',
                  maxHeight: 320,
                  borderRadius: 8,
                  border: '1px solid #ddd',
                  display: 'block',
                }}
              />
              <IconButton
                size="small"
                onClick={() => setPhoto(null)}
                disabled={submitting}
                aria-label="Remove photo"
                sx={{
                  position: 'absolute',
                  top: 6,
                  right: 6,
                  bgcolor: 'rgba(0,0,0,0.6)',
                  color: '#fff',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          ) : (
            <Button
              variant="outlined"
              component="label"
              startIcon={<ImageOutlinedIcon />}
              disabled={submitting}
              sx={{ alignSelf: 'flex-start' }}
            >
              Add Photo
              <input
                hidden
                type="file"
                accept="image/png, image/jpeg"
                onChange={(event) => {
                  handleFileSelected(event.target.files?.[0]);
                  event.target.value = '';
                }}
              />
            </Button>
          )}

          <TextField
            label={photo ? 'Caption (optional)' : 'Text'}
            value={caption}
            onChange={(event) => setCaption(event.target.value)}
            placeholder={photo ? 'Write a caption…' : "What's happening?"}
            fullWidth
            multiline
            minRows={3}
            disabled={submitting}
            error={overLimit}
            helperText={`${caption.length}/${captionMax}${
              photo ? '' : ' — add a photo for a longer caption'
            }`}
            sx={{ '& .MuiInputBase-input:not(:disabled)': { color: 'black' } }}
          />

          <Typography variant="body2" color="text.secondary">
            Publishes immediately to every follower&apos;s home feed and to @pagent&apos;s
            profile. It does not enter matchmaking.
          </Typography>

          {error && <Typography color="error">{error}</Typography>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" color="primary" disabled={!canPost}>
          {submitting ? 'Publishing…' : 'Publish'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ManagePagentPosts = () => {
  const [loading, setLoading] = useState(false);
  const [account, setAccount] = useState(null);
  const [posts, setPosts] = useState([]);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [composerOpen, setComposerOpen] = useState(false);
  const [postToDelete, setPostToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchPosts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await AxiosInstance.get('/admin/pagent-posts', {
        params: { page: page + 1, limit: ROWS_PER_PAGE },
      });
      const data = response.data?.data || {};
      setAccount(data.account || null);
      setPosts(data.posts || []);
      setTotalCount(data.pagination?.totalCount || 0);
    } catch (error) {
      console.error('Error fetching @pagent posts:', error);
      toast.error(error?.response?.data?.message || 'Failed to load @pagent posts');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const handleConfirmDelete = async () => {
    if (!postToDelete) return;
    try {
      setDeleting(true);
      await AxiosInstance.delete(`/admin/pagent-posts/${postToDelete._id}`);
      toast.success('Post deleted');
      setPostToDelete(null);
      fetchPosts();
    } catch (error) {
      console.error('Error deleting @pagent post:', error);
      toast.error(error?.response?.data?.message || 'Failed to delete post');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <PageLayout
      title="@pagent Posts"
      subtitle="Photo and text posts published from the official account"
      loading={loading}
      actions={
        <Button
          variant="contained"
          color="primary"
          onClick={() => setComposerOpen(true)}
          startIcon={<AddIcon />}
        >
          New Post
        </Button>
      }
    >
      <Box
        sx={{
          padding: '15px 15px 0 15px',
          borderRadius: '6px',
          boxShadow: 'rgba(0, 0, 0, 0.05) 0 6px 24px 0, rgba(0, 0, 0, 0.08) 0 0 0 1px',
        }}
      >
        {account && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <Avatar src={account.profilePicture || undefined} sx={{ width: 36, height: 36 }}>
              P
            </Avatar>
            <Box>
              <Typography sx={{ fontWeight: 600, lineHeight: 1.2 }}>@{account.username}</Typography>
              <Typography variant="body2" color="text.secondary">
                {totalCount} post{totalCount === 1 ? '' : 's'}
              </Typography>
            </Box>
          </Box>
        )}

        <TableContainer
          component={Paper}
          sx={{
            maxHeight: 'calc(100vh - 290px)',
            boxShadow: 'none',
            border: '1px solid rgba(224, 224, 224, 1)',
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...headCellSx, width: 120 }}>Photo</TableCell>
                <TableCell sx={headCellSx}>Caption</TableCell>
                <TableCell sx={{ ...headCellSx, width: 90 }}>Type</TableCell>
                <TableCell sx={{ ...headCellSx, width: 80 }} align="right">
                  Likes
                </TableCell>
                <TableCell sx={{ ...headCellSx, width: 100 }} align="right">
                  Comments
                </TableCell>
                <TableCell sx={{ ...headCellSx, width: 200 }}>Posted</TableCell>
                <TableCell sx={{ ...headCellSx, width: 70 }} align="center">
                  Delete
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {posts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ color: '#666', py: 5 }}>
                    {loading ? 'Loading…' : 'No posts yet. Use “New Post” to publish one.'}
                  </TableCell>
                </TableRow>
              ) : (
                posts.map((post) => (
                  <TableRow key={post._id} hover>
                    <TableCell>
                      {post.media ? (
                        <a href={post.media} target="_blank" rel="noreferrer">
                          <img
                            src={post.thumbnail || post.media}
                            alt=""
                            style={{
                              width: 96,
                              height: 96,
                              objectFit: 'cover',
                              borderRadius: 6,
                              border: '1px solid #ddd',
                              display: 'block',
                            }}
                          />
                        </a>
                      ) : (
                        <Box
                          sx={{
                            width: 96,
                            height: 96,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: 1,
                            border: '1px dashed #ccc',
                            color: '#aaa',
                            fontSize: 12,
                          }}
                        >
                          Text
                        </Box>
                      )}
                    </TableCell>
                    <TableCell sx={{ color: '#222', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {post.caption || <span style={{ color: '#999' }}>—</span>}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={post.type === 'MEDIA_POST' ? 'Photo' : 'Text'}
                        color={post.type === 'MEDIA_POST' ? 'primary' : 'default'}
                        variant="outlined"
                      />
                      {post.isDisabled && (
                        <Chip size="small" label="Disabled" color="warning" sx={{ ml: 0.5 }} />
                      )}
                    </TableCell>
                    <TableCell align="right">{post.likeCount}</TableCell>
                    <TableCell align="right">{post.commentCount}</TableCell>
                    <TableCell sx={{ color: '#444' }}>
                      {dayjs(post.createdAt).format('ddd, MMM D, YYYY @ hh:mm A')}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Delete post">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setPostToDelete(post)}
                          aria-label="Delete post"
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <CommonPagination
          count={totalCount}
          rowsPerPage={ROWS_PER_PAGE}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
        />
      </Box>

      <NewPostDialog
        open={composerOpen}
        onClose={() => setComposerOpen(false)}
        onPublished={() => {
          toast.success('Published as @pagent');
          if (page === 0) fetchPosts();
          else setPage(0);
        }}
      />

      <Dialog
        open={Boolean(postToDelete)}
        onClose={deleting ? undefined : () => setPostToDelete(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete @pagent post</DialogTitle>
        <DialogContent>
          <Typography color="error">
            This removes the post from every feed and from @pagent&apos;s profile. Likes and
            comments on it will no longer be visible.
          </Typography>
          {postToDelete?.caption && (
            <Typography sx={{ mt: 1.5, color: '#444', whiteSpace: 'pre-wrap' }}>
              “{postToDelete.caption}”
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPostToDelete(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained" disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <ToastContainer position="top-right" autoClose={3000} />
    </PageLayout>
  );
};

export default ManagePagentPosts;
